// Centralized theme and styles for Lead Dashboard and its widgets

export const theme = {
  colors: {
    background: "#f8f9fa",
    surface: "#ffffff",
    primary: "#6366f1",
    primaryHover: "#4f46e5",
    danger: "#EF4444",
    dangerHover: "#DC2626",
    green: "#4CAF50",
    red: "#FF5252",
    text: "#1f2937",
    textSecondary: "#4b5563",
    textMuted: "#6b7280",
    border: "#e5e7eb",
    borderMedium: "#d1d5db",
    grid: "#f3f4f6",
  },
  // Professional chart color palette - harmonious and modern
  chartColors: {
    // Primary palette for multi-series charts
    palette: [
      "#6366f1", // Indigo
      "#10b981", // Emerald
      "#f59e0b", // Amber
      "#ec4899", // Pink
      "#8b5cf6", // Violet
      "#06b6d4", // Cyan
      "#f97316", // Orange
      "#84cc16", // Lime
    ],
    // Specific use colors
    primary: "#6366f1",
    secondary: "#10b981",
    tertiary: "#f59e0b",
    // Line chart specific
    line1: "#6366f1",
    line2: "#10b981",
    // Bar chart
    bar: "#8b5cf6",
    // Pie/Donut
    pie: ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4"],
    // Grid and axis
    gridLine: "#f0f0f0",
    axisLine: "#e5e7eb",
    axisText: "#9ca3af",
  },
  fonts: {
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  shadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  borderRadius: "12px",
};

// Common chart configuration
export const chartConfig = {
  animation: {
    duration: 800,
    easing: "ease-out",
  },
  tooltip: {
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
    padding: "12px 16px",
  },
  grid: {
    stroke: theme.chartColors.gridLine,
    strokeDasharray: "0",
    vertical: false,
  },
  axis: {
    tickFontSize: 11,
    tickColor: theme.chartColors.axisText,
    lineColor: theme.chartColors.axisLine,
  },
};

export const styles = {
  dashboard: {
    padding: "16px",
    backgroundColor: theme.colors.background,
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
  },
  header: {
    fontSize: "1.75em",
    fontWeight: 600,
    color: theme.colors.text,
    marginBottom: "16px",
  },
  kpiCard: {
    border: '1px solid ' + theme.colors.border,
    padding: '24px',
    borderRadius: theme.borderRadius,
    textAlign: 'center',
    backgroundColor: theme.colors.surface,
    boxShadow: theme.shadow,
  },
  kpiTitle: {
    margin: 0,
    color: theme.colors.textMuted,
    fontSize: "1em",
    fontWeight: 600,
  },
  kpiValue: {
    fontSize: "1.8em",
    margin: "8px 0",
    fontWeight: "bold",
    color: theme.colors.text,
    lineHeight: 1.2,
  },
  kpiSubValue: {
    fontSize: "0.9em",
    color: theme.colors.textSecondary,
    margin: "0",
  },
  chartWidget: {
    backgroundColor: theme.colors.surface,
    padding: '24px',
    borderRadius: theme.borderRadius,
    height: '100%',
    position: 'relative',
    boxShadow: theme.shadow,
    display: 'flex',
    flexDirection: 'column',
  },
  chartWidgetHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexShrink: 0,
  },
  chartWidgetTitle: {
    margin: 0,
    fontSize: "1.1em",
    fontWeight: 600,
    color: theme.colors.text,
  },
  chartWidgetExpandButton: {
    background: theme.colors.primary,
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
    transition: "background 0.2s",
  },
  chartWidgetContent: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "250px",
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 9999,
    padding: '24px',
    overflow: 'auto',
  },
  modalContent: {
    backgroundColor: theme.colors.surface,
    borderRadius: '16px',
    width: '95%',
    maxWidth: '1600px',
    margin: '0 auto',
    padding: '32px',
    position: 'relative',
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    paddingBottom: "16px",
    borderBottom: `2px solid ${theme.colors.border}`,
  },
  modalTitle: {
    margin: 0,
    fontSize: "2em",
    color: theme.colors.text,
    fontWeight: 600,
  },
  modalCloseButton: {
    background: theme.colors.danger,
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    fontSize: "24px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s",
  },
  modalChartContainer: {
    height: "500px",
    marginBottom: "32px",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius,
    padding: "24px",
    backgroundColor: theme.colors.background,
  },
  modalDataContainer: {
    marginTop: "32px",
  },
  modalDataHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  modalDataTitle: {
    margin: 0,
    color: theme.colors.text,
    fontSize: "1.4em",
    fontWeight: 600,
  },
  modalDataBadge: {
    backgroundColor: theme.colors.primary,
    color: "white",
    padding: "6px 12px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
  },
  modalTableContainer: {
    overflowX: 'auto',
    border: '1px solid ' + theme.colors.border,
    borderRadius: theme.borderRadius,
    maxHeight: '400px',
    overflowY: 'auto',
  },
  modalTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  modalThead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  modalTrHeader: {
    backgroundColor: theme.colors.grid,
  },
  modalTh: {
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '2px solid ' + theme.colors.borderMedium,
    fontWeight: 600,
    color: theme.colors.textSecondary,
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    backgroundColor: theme.colors.grid,
  },
  modalTrBody: {
    backgroundColor: theme.colors.surface,
    transition: "background-color 0.2s",
  },
  modalTd: {
    padding: '12px 16px',
    borderBottom: '1px solid ' + theme.colors.border,
    color: theme.colors.textSecondary,
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  mainGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
    gap: "16px",
  },
  // Empty state styling
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: "200px",
    color: theme.colors.textMuted,
    fontSize: "14px",
  },
  emptyStateIcon: {
    fontSize: "48px",
    marginBottom: "12px",
    opacity: 0.5,
  },
};
